<script setup lang="ts">
  import type { DocumentSendListItem } from '@/entities/DocumentSend';
  import type { OrderOptions } from '@/shared/lib';
  import {
    DefaultTable,
    HeaderTableWithButtons,
    NoDocuments,
    SearchNoResults,
    TableHeader,
    TableLoading,
  } from '@/shared/ui';
  import NoDocumentsIcon from '@/shared/assets/images/no-documents.svg';

  import DocumentSendControlTableRow from './DocumentSendControlTableRow.vue';

  const TABLE_HEADERS = [
    'Título',
    'Data de envio',
    'Remetente',
    'Destinatário(s)',
    'Formatos',
    'Ações',
  ];

  const props = withDefaults(
    defineProps<{
      documentSends?: DocumentSendListItem[];
      showLoadingState?: boolean;
      shouldShowEmptyState?: boolean;
      shouldShowNoResults?: boolean;
      orderOptions?: OrderOptions[];
      isFilterActive?: boolean;
    }>(),
    {
      documentSends: () => [],
      showLoadingState: false,
      shouldShowEmptyState: false,
      shouldShowNoResults: false,
      orderOptions: () => [],
      isFilterActive: false,
    }
  );

  const emit = defineEmits<{
    (e: 'update:sort', sort: string, order: string): void;
    (e: 'filter-clicked'): void;
    (e: 'view-clicked', id: number): void;
    (e: 'delete-clicked', id: number): void;
  }>();

  const getHeaderClass = (header: string) =>
    header === 'Ações'
      ? 'px-6 py-4.5 text-right text-xs font-semibold text-gray-07 whitespace-nowrap align-middle w-[140px]'
      : 'pl-6 pr-4 py-4.5 text-left text-xs font-semibold text-gray-07 whitespace-nowrap align-middle';
</script>

<template>
  <DefaultTable data-testid="document-send-control-table">
    <thead>
      <tr>
        <HeaderTableWithButtons
          id="header-table-with-buttons"
          colspan="6"
          text="Controle de Envio"
          :order-options="props.orderOptions"
          :is-filter-active="props.isFilterActive"
          @update:sort="(sort, order) => emit('update:sort', sort, order)"
          @filter-clicked="emit('filter-clicked')"
        />
      </tr>
      <tr v-if="!props.showLoadingState && props.documentSends.length > 0">
        <TableHeader
          v-for="header in TABLE_HEADERS"
          :key="header"
          :text="header"
          :th-class="getHeaderClass(header)"
        />
      </tr>
    </thead>

    <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
      <tr v-if="props.showLoadingState">
        <td colspan="6">
          <TableLoading
            data-testid="document-send-control-loading"
            :row-size="5"
            :column-size="6"
          />
        </td>
      </tr>
      <tr v-else-if="props.shouldShowNoResults">
        <td
          colspan="6"
          class="py-8"
        >
          <SearchNoResults data-testid="document-send-control-no-results" />
        </td>
      </tr>
      <tr v-else-if="props.shouldShowEmptyState">
        <td
          colspan="6"
          class="px-6 py-8"
        >
          <NoDocuments
            data-testid="document-send-control-empty"
            title="Ops! Parece que ainda não há envios"
            :icon="NoDocumentsIcon"
          />
        </td>
      </tr>
      <template v-else>
        <DocumentSendControlTableRow
          v-for="documentSend in props.documentSends"
          :key="documentSend.id"
          :item="documentSend"
          @view-clicked="(id) => emit('view-clicked', id)"
          @delete-clicked="(id) => emit('delete-clicked', id)"
        />
      </template>
    </tbody>
  </DefaultTable>
</template>
